"use client"
import { addItemToCart } from "@/actions/selection/add-item-to-selection"
import { deleteSelectionOrderLine } from "@/actions/selection/delete-orderline-selection"
import { keepSelection } from "@/actions/selection/keep-selection"
import { reduceSelectionQuantity } from "@/actions/selection/reduce-selection"
import { removeVoucher } from "@/actions/selection/remove-voucher"
import Heading from "@/app/(site)/_components/Atoms/Heading"
import IconArrowRight from "@/app/(site)/_components/Atoms/Icons/IconArrowRight"
import IconBin from "@/app/(site)/_components/Atoms/Icons/IconBin"
import IconChevronDown from "@/app/(site)/_components/Atoms/Icons/IconChevronDown"
import IconChevronUp from "@/app/(site)/_components/Atoms/Icons/IconChevronUp"
import IconClose from "@/app/(site)/_components/Atoms/Icons/IconClose"
import StoryText from "@/app/(site)/_components/Atoms/Storytext"
import Text from "@/app/(site)/_components/Atoms/Text"
import { ReduceSelectionQuantity } from "@/app/(site)/_components/Modules/Cart"
import cartHelper from "@/app/(site)/_components/Modules/Checkout/CartAside/helpers"
import ButtonLink from "@/app/(site)/_components/Molecules/ButtonLink"
import PromotionCode from "@/app/(site)/_components/Molecules/PromotionCode"
import formatCurrency from "@/app/(site)/_utils/_hooks/priceFormatter"
import { getLangCode } from "@/app/(site)/_utils/misc"
import { useCheckoutForm } from "@/app/(site)/contexts/checkout"
import { isPfasShippingStateAndZipRestricted } from "@/data/pfas-zipcode-state-restrictions"
import { ProductData } from "@/lib/content-service/types/ContentServiceProduct"
import { FREE_SHIPPING_VOUCHER_CODE } from "@/lib/siteconfig/constants"
import { addToCartDepictEvent } from "@/lib/utils/tracking/depictEvents"
import { addToCartEvent } from "@/lib/utils/tracking/gtmEvents"
import { t } from "@/translations"
import {
    CheckoutAPIProduct,
    RelatedProduct
} from "@/types/centra/checkout-api/products/post-products"
import { Err, Ok } from "@/types/generic/Result"
import placeholder from "@assets/images/placeholder.png"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import Image from "next/image"
import { usePathname } from "next/navigation"
import React from "react"
import toast from "react-hot-toast"
import { match } from "ts-pattern"

export default function CartAside() {
    const path = usePathname()
    const queryClient = useQueryClient()
    const locale = path.split("/")[1]
    const langCode = getLangCode(locale) ?? ""
    const { customerInfo, updateCustomerInfo } = useCheckoutForm()

    const [showSummary, setShowSummary] = React.useState(true)

    const { data } = useQuery({
        queryKey: ["selection"],
        queryFn: () => keepSelection()
    })

    const cart = React.useMemo(() => {
        if (!data) return null
        return match(data)
            .with(Ok, value => value.data)
            .with(Err, () => {
                toast.error("An error occurred while retrieving cart data")
                return null
            })
            .exhaustive()
    }, [data, langCode])

    React.useEffect(() => {
        if (!cart?.checkoutApiSelection?.selection.items) return
        const shippingState = customerInfo.shippingAddress?.state
        const shippingZip = customerInfo.shippingAddress?.zipCode
        const shippingCountry = customerInfo.shippingAddress?.country
        const cartItems = cart.checkoutApiSelection.selection.items

        // Check if the shipping state is restricted based on product state restrictions and
        // if the shipping zip is restricted in combination with the state restriction
        const shouldRestrictCheckout = cartItems.some(item =>
            isPfasShippingStateAndZipRestricted(
                item?.product?.pfas_state_restriction,
                shippingState,
                shippingZip,
                shippingCountry
            )
        )
        if (shouldRestrictCheckout !== customerInfo.pfasRestrictedProduct) {
            updateCustomerInfo({ pfasRestrictedProduct: shouldRestrictCheckout })
        }
    }, [
        cart,
        customerInfo.shippingAddress.state,
        customerInfo.shippingAddress.zipCode,
        customerInfo.pfasRestrictedProduct,
        customerInfo.shippingAddress?.country,
        updateCustomerInfo
    ])

    const step = path.split("/")[3]
    const isStepShippingOrPayment =
        step === "shipping" || step === "payment" || step === "payment-result"

    if (!cart) return <></>

    const {
        isCartEmpty,
        sumTotalPriceBeforeDiscount,
        salesTotal,
        totalDiscountPriceAsNumber = 0,
        currency,
        itemsTotalPriceAsNumber,
        vouchers,
        isVoucherApplied,
        isSessionDiscounted,
        priceWithoutShippingAsNumber,
        shippingCountry
    } = cartHelper(cart)

    const handleItemAdd = async (
        cartItem: { item: string; sku: string; size: string },
        product: CheckoutAPIProduct | RelatedProduct | ProductData
    ) => {
        const previousCart = queryClient.getQueryData(["selection"])
        const updatedCart = { ...(previousCart as any) }
        queryClient.setQueryData(["selection"], updatedCart)

        try {
            const formData = new FormData()
            formData.append("item", cartItem?.item)
            await addItemToCart(formData)
            addToCartEvent(product, cartItem)
            addToCartDepictEvent(product, cartItem)
        } catch (error) {
            queryClient.setQueryData(["selection"], previousCart)
        } finally {
            queryClient.invalidateQueries({ queryKey: ["selection"] })
        }
    }

    const handleItemDecrease = async (lineItem: string) => {
        const previousCart = queryClient.getQueryData(["selection"])
        const updatedCart = { ...(previousCart as any) }
        queryClient.setQueryData(["selection"], updatedCart)

        try {
            const formData: ReduceSelectionQuantity =
                new FormData() as ReduceSelectionQuantity
            formData.append("item", lineItem)
            await reduceSelectionQuantity(formData)
        } catch (error) {
            queryClient.setQueryData(["selection"], previousCart)
        } finally {
            queryClient.invalidateQueries({ queryKey: ["selection"] })
        }
    }

    const handleOrderLineDelete = async (lineId: string) => {
        const previousCart = queryClient.getQueryData(["selection"])
        const updatedCart = { ...(previousCart as any) }
        queryClient.setQueryData(["selection"], updatedCart)

        try {
            const res = await deleteSelectionOrderLine(lineId)
            match(res)
                .with(Ok, () => {
                    queryClient.invalidateQueries({ queryKey: ["selection"] })
                })
                .with(Err, () => {
                    queryClient.setQueryData(["selection"], previousCart)
                })
                .exhaustive()
        } catch (error) {
            queryClient.setQueryData(["selection"], previousCart)
        } finally {
            queryClient.invalidateQueries({ queryKey: ["selection"] })
        }
    }

    async function handleVoucherDelete(voucher: string) {
        try {
            await removeVoucher({ voucher: voucher })
        } catch (error) {
            console.error(error)
        } finally {
            queryClient.invalidateQueries({ queryKey: ["selection"] })
        }
    }

    if (isCartEmpty) {
        return (
            <div className="flex items-center justify-center">
                <div className="flex flex-col items-center justify-center py-200">
                    <Heading type="h2" className="pb-32">
                        {t(langCode, "cart.cart_empty")}
                    </Heading>
                    <ButtonLink
                        href="/"
                        prefetch={false}
                        color="black"
                        variant="primary"
                        size="large"
                    >
                        {t(langCode, "cart.continue_shopping")}
                        <IconArrowRight aria-hidden="true" className="h-20 w-20" />
                    </ButtonLink>
                </div>
            </div>
        )
    }

    return (
        <div>
            <Heading
                type="displayTrim"
                className="mb-40 block !text-40 md:hidden md:text-48"
            >
                {t(langCode, "checkout.information")}
            </Heading>
            <div className="mt-0 border-b border-b-black pb-24 md:mt-120">
                <div className="border-b border-b-black">
                    <div className="flex items-center justify-between border-y border-t-black">
                        {showSummary ? (
                            <div
                                onClick={() => setShowSummary(!showSummary)}
                                className="cursor-pointer py-16"
                            >
                                <div className="flex items-center">
                                    <Text
                                        size="medium"
                                        weight="regular"
                                        className="select-none break-words !text-14"
                                    >
                                        {t(langCode, "checkout.hide_summary")}
                                    </Text>
                                    <IconChevronUp className="ml-8 h-16 w-16 flex-shrink-0" />
                                </div>
                            </div>
                        ) : (
                            <div
                                onClick={() => setShowSummary(!showSummary)}
                                className="cursor-pointer py-16"
                            >
                                <div className="flex items-center">
                                    <Text
                                        size="medium"
                                        weight="regular"
                                        className="select-none break-words !text-14"
                                    >
                                        {t(langCode, "checkout.show_summary")}
                                    </Text>
                                    <IconChevronDown className="ml-8 h-16 w-16 flex-shrink-0" />
                                </div>
                            </div>
                        )}
                        <div className="flex flex-col items-end justify-end py-16">
                            <Text
                                size="medium"
                                weight="regular"
                                className="!text-14 text-main-dark-grey"
                            >
                                {
                                    cart?.checkoutApiSelection?.selection?.totals
                                        ?.itemsTotalPrice
                                }
                            </Text>
                            {isSessionDiscounted && (
                                <Text
                                    size="medium"
                                    weight="regular"
                                    className="!text-14 text-main-dark-grey line-through"
                                >
                                    {formatCurrency({
                                        amount: sumTotalPriceBeforeDiscount,
                                        currency: currency ?? ""
                                    })}
                                </Text>
                            )}
                        </div>
                    </div>
                    {showSummary && (
                        <div className="w-full">
                            {cart?.checkoutApiSelection?.selection.items?.map(
                                ({ item, line, product, size, quantity, sku }, index) => {
                                    if (!item) {
                                        return null
                                    }
                                    const productLensName = product?.lens?.lens_name
                                    const variantName = product?.variantName
                                    const name = product?.name
                                    const thumbnail = product?.media?.standard?.[0]
                                    const priceBeforeDiscount =
                                        product?.priceBeforeDiscount
                                    const price = product?.price
                                    const showAsOnSale = product?.showAsOnSale
                                    const isPfasRestricted =
                                        isPfasShippingStateAndZipRestricted(
                                            product?.pfas_state_restriction,
                                            customerInfo.shippingAddress?.state,
                                            customerInfo.shippingAddress?.zipCode,
                                            customerInfo.shippingAddress?.country
                                        )

                                    return (
                                        <div
                                            key={`${index}-${line}`}
                                            className="flex w-full gap-16 py-16"
                                        >
                                            <div>
                                                <Image
                                                    className={`bg-main-medium-grey ${isPfasRestricted ? "opacity-30" : ""}`}
                                                    src={thumbnail ?? placeholder}
                                                    width={288}
                                                    height={288}
                                                    alt={name}
                                                />
                                            </div>
                                            <div className="flex w-full flex-col justify-between">
                                                <div>
                                                    {isPfasRestricted && (
                                                        <Text
                                                            size="extrasmall"
                                                            weight="regular"
                                                            className="pb-8 text-warning-red"
                                                        >
                                                            {t(
                                                                langCode,
                                                                "restricted_product_message_cart"
                                                            )}
                                                        </Text>
                                                    )}
                                                    <Text
                                                        size="extrasmall"
                                                        weight="regular"
                                                        className={`${isPfasRestricted ? "text-main-light-grey-disabled" : ""}`}
                                                    >
                                                        {name}
                                                    </Text>
                                                    <Text
                                                        size="extrasmall"
                                                        weight="regular"
                                                        className={`${isPfasRestricted ? "text-main-light-grey-disabled" : "text-main-dark-grey"}`}
                                                    >
                                                        {variantName}
                                                    </Text>
                                                    <Text
                                                        size="extrasmall"
                                                        weight="regular"
                                                        className={`${isPfasRestricted ? "text-main-light-grey-disabled" : "text-main-dark-grey"}`}
                                                    >
                                                        {productLensName}
                                                    </Text>
                                                    <Text
                                                        size="extrasmall"
                                                        weight="regular"
                                                        className={`${isPfasRestricted ? "text-main-light-grey-disabled" : "text-main-dark-grey"}`}
                                                    >
                                                        {size}
                                                    </Text>
                                                </div>
                                                <div className="flex justify-between">
                                                    <div>
                                                        {showAsOnSale ? (
                                                            <div className="flex flex-col">
                                                                <Text
                                                                    size="extrasmall"
                                                                    weight="regular"
                                                                    className={`${isPfasRestricted ? "text-main-light-grey-disabled" : "text-black"}`}
                                                                >
                                                                    {price}
                                                                </Text>
                                                                <Text
                                                                    size="extrasmall"
                                                                    weight="regular"
                                                                    className={`${isPfasRestricted ? "text-main-light-grey-disabled line-through" : "text-main-dark-grey line-through"}`}
                                                                >
                                                                    {priceBeforeDiscount}
                                                                </Text>
                                                            </div>
                                                        ) : (
                                                            <Text
                                                                size="small"
                                                                weight="regular"
                                                                className={`${isPfasRestricted ? "text-main-light-grey-disabled" : "text-main-dark-grey"}`}
                                                            >
                                                                {price}
                                                            </Text>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex flex-col justify-between">
                                                <div className="flex w-full items-center justify-end">
                                                    {!isStepShippingOrPayment && (
                                                        <div
                                                            onClick={() =>
                                                                handleOrderLineDelete(
                                                                    line
                                                                )
                                                            }
                                                            className="flex h-40 w-40 cursor-pointer items-center justify-center"
                                                        >
                                                            <IconBin
                                                                className={`${isPfasRestricted ? "text-warning-red" : "text-current"} h-16 w-16`}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                                {!isStepShippingOrPayment && (
                                                    <div
                                                        className={`flex items-center ${isPfasRestricted ? "text-main-light-grey-disabled" : ""} `}
                                                    >
                                                        <button
                                                            disabled={isPfasRestricted}
                                                            className={`px-16 ${isPfasRestricted ? "cursor-not-allowed" : "cursor-pointer"}`}
                                                            onClick={() =>
                                                                handleItemDecrease(line)
                                                            }
                                                        >
                                                            -
                                                        </button>
                                                        <Text
                                                            size="small"
                                                            weight="regular"
                                                        >
                                                            {quantity}
                                                        </Text>
                                                        <button
                                                            disabled={isPfasRestricted}
                                                            className={`px-16 ${isPfasRestricted ? "cursor-not-allowed" : "cursor-pointer"}`}
                                                            onClick={() =>
                                                                handleItemAdd(
                                                                    { item, sku, size },
                                                                    product
                                                                )
                                                            }
                                                        >
                                                            +
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )
                                }
                            )}
                        </div>
                    )}
                </div>
                <div className="py-24 md:py-32">
                    <PromotionCode />
                </div>
                <div className="mb-16 flex justify-between gap-10">
                    <Text size="small" weight="regular" className="text-main-dark-grey">
                        {t(langCode, "checkout.subtotal")}
                    </Text>
                    <Text size="small" weight="regular" className="text-main-dark-grey">
                        {formatCurrency({
                            amount: sumTotalPriceBeforeDiscount,
                            locale: locale,
                            currency: currency ?? ""
                        })}
                    </Text>
                </div>

                {isVoucherApplied &&
                    vouchers.map((voucher, index) => {
                        const voucherCode = voucher?.voucher ?? ""
                        const priceOff = voucher?.priceOff
                        if (voucherCode === FREE_SHIPPING_VOUCHER_CODE) return null
                        return (
                            <div
                                key={index}
                                className="mb-16 flex justify-between gap-10"
                            >
                                <div className="flex flex-none gap-8">
                                    <Text
                                        size="small"
                                        weight="regular"
                                        className="text-main-dark-grey"
                                    >
                                        Voucher Code &quot;{voucherCode}&quot;
                                    </Text>
                                    <button
                                        onClick={() => handleVoucherDelete(voucherCode)}
                                    >
                                        <IconClose className="h-16 w-16" />
                                    </button>
                                </div>
                                <Text
                                    size="small"
                                    weight="regular"
                                    className="text-main-dark-grey"
                                >
                                    {priceOff}
                                </Text>
                            </div>
                        )
                    })}
                <div className="mb-24 flex justify-between text-main-dark-grey">
                    <Text size="small" weight="regular">
                        {t(langCode, "checkout.shipping")}
                    </Text>
                    {step !== "payment" && step !== "shipping" ? (
                        <StoryText size="size4" className="text-14 text-main-dark-grey">
                            {t(langCode, "checkout.to_be_calculated")}
                        </StoryText>
                    ) : (
                        <StoryText size="size4" className="text-main-dark-grey">
                            {cart?.checkoutApiSelection?.selection?.totals?.shippingPrice}
                        </StoryText>
                    )}
                </div>
                {totalDiscountPriceAsNumber < 0 && (
                    <div className="mb-24 flex justify-between text-main-dark-grey">
                        <Text size="small" weight="regular">
                            {t(langCode, "checkout.sale_total")}
                        </Text>
                        <StoryText size="size4" className="text-main-dark-grey">
                            {formatCurrency({
                                amount: salesTotal,
                                currency: currency ?? ""
                            })}
                        </StoryText>
                    </div>
                )}

                <div className="mb-8 flex items-center justify-between">
                    <StoryText size="size3" className="text-24">
                        {t(langCode, "checkout.total")}
                    </StoryText>
                    {step !== "payment" && step !== "shipping" ? (
                        <Text size="medium" weight="regular" className="text-24">
                            {formatCurrency({
                                amount: priceWithoutShippingAsNumber,
                                locale: locale,
                                currency: currency ?? ""
                            })}
                        </Text>
                    ) : (
                        <Text size="medium" weight="regular" className="text-24">
                            {
                                cart?.checkoutApiSelection?.selection?.totals
                                    ?.grandTotalPrice
                            }
                        </Text>
                    )}
                </div>
                <div>
                    <StoryText size="size4" className="text-main-dark-grey">
                        {(shippingCountry === "US" || shippingCountry === "CA") &&
                        step !== "shipping" &&
                        step !== "payment"
                            ? t(langCode, "checkout.taxes_calculated")
                            : `${t(langCode, "checkout.including")} ${cart?.checkoutApiSelection?.selection?.totals?.grandTotalPriceTax} ${t(langCode, "checkout.taxes")}`}
                    </StoryText>
                </div>
            </div>
        </div>
    )
}
